import { AppContext } from '@prisjakt/ui-app-context';
import { noop } from '@prisjakt/ui-base';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { ViewportTracker } from '@prisjakt/ui-viewport';
import queryString from 'querystring';
import React, { memo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppTransitionRouter } from './app-transition-router';
import * as S from './appStyles';
import Feedback from './areas/feedback';
import Footer from './areas/footer';
import MainHeader from './areas/header';
import Sidebar from './areas/sidebar/components/sidebar-desktop';
import addPerformanceTiming from './client/performance-reporter';
import { CoBrandingBanner } from './components/co-branding-banner';
import CookieBanner from './components/cookie-banner';
import CookieBannerSourcePoint from './components/cookie-banner-sourcepoint';
import ErrorBoundary from './components/error-boundary';
import ErrorMessage from './components/error-message';
import NewsletterSignupModal, {
  NEWSLETTER_PARAM,
} from './components/newsletter-signup-modal';
import { ScrollBehaviourProvider } from './components/scroll-to-top';
import ShutdownMessage from './components/shutdown-message';
import StaticIntercom from './components/static-intercom';
import ToastContainer from './components/toast-container';
import './components/toast-container/sagas/toast';
import { TocModal } from './components/toc-modal';
import { AppState } from './reducers';

type Error = {
  message: string;
  stack: string;
};

const Error500 = () => <ErrorMessage status={500} />;
const Error500WithMessage = (error: Error) => (
  <ErrorMessage message={error.message} stack={error.stack} status={500} />
);

const isRedirectedFromMarketRegex = /(&|^\?)redirect=(ie|it|pl)(&|$)/;

/**
 * A number so that we can track the render after the
 * initial mounting. This is when experiments are loaded and viewportcontext is updated.
 * This is used to add user performance timings.
 */
let renders = 0;

type Props = {
  callback: () => void;
};

const App = ({ callback }: Props) => {
  const location = useLocation();
  const renderToastContainer = useSelector(
    (state: AppState) => state.toast.required
  );
  const { layout = 'default' } = useSelector(
    (state: AppState) => state.globals
  );
  const {
    market: { code, sourcePointCMP },
    renderMode,
  } = useContext(AppContext);

  renders += 1;
  if (renders === 2) {
    addPerformanceTiming('firstRerender');
  }

  const openNewsletterSignup = Object.keys(
    queryString.parse(location.search.substr(1))
  ).includes(NEWSLETTER_PARAM);

  const isRedirectedFromMarket = isRedirectedFromMarketRegex.test(
    location.search
  );
  const redirectedFromMarket =
    isRedirectedFromMarket &&
    (isRedirectedFromMarketRegex.exec(location.search) || [])
      .filter(match => /^(ie|it|pl)$/.test(match))
      .pop();

  const isWebViewLayout = renderMode === 'native';

  const isOnKwlp = location.pathname.startsWith('/s/');

  return (
    <ErrorBoundary onError={Error500}>
      <S.App layout={layout} ref={callback}>
        {openNewsletterSignup && <NewsletterSignupModal />}
        <ExperimentEditor />

        <ViewportTracker>
          {!isWebViewLayout && <MainHeader />}
          {renderToastContainer && <ToastContainer />}
          <Feedback />
          <ErrorBoundary onError={noop}>
            {sourcePointCMP?.enabled ? (
              <CookieBannerSourcePoint />
            ) : (
              <>
                <CoBrandingBanner />
                <CookieBanner />
              </>
            )}
          </ErrorBoundary>
          <ErrorBoundary onError={noop}>
            <StaticIntercom />
          </ErrorBoundary>
          <ErrorBoundary onError={Error500WithMessage}>
            <ScrollBehaviourProvider>
              <S.Main
                data-test="AppMain"
                data-test-market-id={code}
                doWidthTransition={!isOnKwlp}
              >
                {!isWebViewLayout && <Sidebar isOnKwlp={isOnKwlp} />}

                <S.Content>
                  <AppTransitionRouter />
                  {!isWebViewLayout && <Footer />}
                </S.Content>
              </S.Main>
            </ScrollBehaviourProvider>
          </ErrorBoundary>

          <TocModal />

          {isRedirectedFromMarket && (
            <ShutdownMessage redirectedFromMarket={redirectedFromMarket} />
          )}
        </ViewportTracker>
      </S.App>
    </ErrorBoundary>
  );
};

export default memo(App);
